// Locale-aware path helper (spec 045-multilingual-support, Phase 7 / WE-2,
// FR-013 / FR-014). ALWAYS-PREFIX: every in-app navigation MUST carry the
// active locale as the first path segment (`/fi/blog`, `/he/contact`, …) so the
// prefix — the single source of truth for the UI locale (WE-1) — is never lost.
//
// Usage:
//   <RouterLink :to="localePath('/blog')" />        // → /<active>/blog
//   router.push(localePath('/'))                     // → /<active>
//   router.push(localePath(route.fullPath, 'fi'))    // switcher: swap the prefix
//
// `localePath` is idempotent: any existing valid locale prefix on the input is
// stripped before the target locale is applied, so it doubles as the switcher's
// "rewrite ONLY the :locale segment" primitive (FR-013) — the rest of the path,
// the query string, and the hash are preserved verbatim.
import { unref } from 'vue';
import i18n, { supportedLocales, type SupportedLocale } from '@/i18n';

// Matches a leading `/<locale>` segment (and only a *whole* segment) on the
// path portion of a URL. Built from the i18n source of truth so a locale added
// there is recognised here automatically.
const LOCALE_PREFIX_RE = new RegExp(`^/(?:${supportedLocales.join('|')})(?=/|$)`);

/**
 * The currently-active UI locale. Reading the i18n ref's `.value` inside a
 * component render (e.g. a `:to` binding) tracks it, so locale-aware links
 * re-resolve reactively when the language changes.
 */
export function activeLocale(): SupportedLocale {
  return unref(i18n.global.locale) as SupportedLocale;
}

/**
 * Strip a leading `/<locale>` prefix from a path, if present. Always returns a
 * path that starts with `/` (the bare locale root `/fi` → `/`). Query/hash are
 * left untouched (the prefix only ever lives at the start of the path part).
 */
export function stripLocalePrefix(to: string): string {
  const [, pathPart = '/', tail = ''] = /^([^?#]*)(.*)$/.exec(to) ?? [];
  const stripped = pathPart.replace(LOCALE_PREFIX_RE, '') || '/';
  return `${stripped.startsWith('/') ? stripped : `/${stripped}`}${tail}`;
}

/**
 * Prepend the active (or given) locale prefix to an internal path. Idempotent:
 * an already-prefixed input has its prefix swapped to the target locale, so the
 * same helper builds links AND powers the language switcher's prefix-swap.
 *
 * Only call this for INTERNAL app paths. External URLs (Tesla OAuth, Stripe
 * redirects) and absolute `http(s)://` links must NOT be passed through.
 */
export function localePath(to: string, locale?: SupportedLocale): string {
  const loc = locale ?? activeLocale();
  const [, pathPart = '/', tail = ''] = /^([^?#]*)(.*)$/.exec(to) ?? [];
  // Drop any existing valid locale prefix first (idempotency + prefix-swap).
  let path = pathPart.replace(LOCALE_PREFIX_RE, '');
  if (!path.startsWith('/')) path = `/${path}`;
  const prefixed = path === '/' ? `/${loc}` : `/${loc}${path}`;
  return `${prefixed}${tail}`;
}
